import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminSidebarLayout } from '../../components/AdminSidebar';
import Step1 from '../../components/admins/new-gig/Step1';
import Step2 from '../../components/admins/new-gig/Step2';
import Step3 from '../../components/admins/new-gig/Step3';
import { getGigById } from '../../services/gig.service';

const EMPTY_PRICING_PKG = { title: '', description: '', deliveryDays: '', price: '' };

const EditGig = () => {
  const { gigId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keywords: [],
    selectedHellocians: [],
    selectedCategories: [],
    images: [],
    pricing: {
      basic:    { ...EMPTY_PRICING_PKG },
      standard: { ...EMPTY_PRICING_PKG },
      advanced: { ...EMPTY_PRICING_PKG },
      pro:      { ...EMPTY_PRICING_PKG },
      premium:  { ...EMPTY_PRICING_PKG },
    },
    addons: [{ name: 'Unlimited Revisions', selected: [false, false, false, false, false] }]
  });

  const { data, isLoading } = useQuery({
    queryKey: ['gigForEdit', gigId],
    queryFn: () => getGigById(gigId),
    enabled: !!gigId,
  });

  // Pre-populate form when gig data arrives
  useEffect(() => {
    const gig = data?.gig;
    if (!gig) return;

    // Map pricing — deliveryTimeframe → deliveryDays
    const pricing = { ...formData.pricing };
    ['basic', 'standard', 'advanced', 'pro', 'premium'].forEach((pkg) => {
      const p = gig.pricing?.[pkg];
      if (p && p.title) {
        pricing[pkg] = {
          title:        p.title || '',
          description:  p.description || '',
          deliveryDays: p.deliveryTimeframe || '',
          price:        p.price?.toString() || '',
        };
      }
    });

    // Addons
    const addons = gig.addons?.length
      ? gig.addons.map((a) => ({
          name: a.name,
          selected: [
            a.values?.basic    === 'included',
            a.values?.standard === 'included',
            a.values?.advanced === 'included',
            a.values?.pro      === 'included',
            a.values?.premium  === 'included',
          ],
        }))
      : formData.addons;

    // Build images array
    const existingImages = [];
    if (gig.cover && !gig.cover.includes('placehold.co')) existingImages.push({ preview: gig.cover });
    if (gig.firstThumbnail) existingImages.push({ preview: gig.firstThumbnail });
    if (gig.secondThumbnail) existingImages.push({ preview: gig.secondThumbnail });
    if (gig.thirdThumbnail) existingImages.push({ preview: gig.thirdThumbnail });

    setFormData({
      title:               gig.title || '',
      description:         gig.description || '',
      keywords:            gig.tags?.map((t) => t._id || t) || [],
      selectedHellocians:  gig.hellocians?.map((h) => h._id || h) || [],
      selectedCategories:  gig.category?.map((c) => c._id || c) || [],
      images:              existingImages,
      pricing,
      addons,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isNextEnabled = () => {
    if (currentStep === 1) {
      return formData.title && formData.selectedHellocians.length > 0 && formData.selectedCategories.length > 0;
    }
    return true;
  };

  if (isLoading) {
    return (
      <AdminSidebarLayout currentPath="/admins/gigs">
        <div className="min-h-screen flex items-center justify-center text-gray-400">
          <div className="w-10 h-10 border-4 border-[#174568] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout currentPath="/admins/gigs">
      <div className="bg-gray-50 min-h-screen font-['Poppins']">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <div className="text-sm breadcrumbs mb-4 text-gray-500">
            <span
              className="cursor-pointer hover:underline"
              onClick={() => navigate('/admins/gigs')}
            >
              Gigs
            </span>
            {' / '}
            {currentStep === 1 ? <span>Edit Gig</span>
              : currentStep === 2 ? <span>Description</span>
              : <span>Pricing</span>}
          </div>

          <h1 className="text-3xl font-bold text-[#174568] mb-8">
            Edit Gig — {currentStep === 1 ? 'Details' : currentStep === 2 ? 'Description' : 'Pricing'}
          </h1>

          {/* Step Indicator */}
          <div className="flex items-center mb-8">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold
                    ${currentStep >= step ? 'bg-[#174568] text-white' : 'bg-gray-200 text-gray-500'}`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step ? 'bg-[#174568]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {currentStep === 1 && (
            <Step1
              formData={formData}
              setFormData={setFormData}
              fileInputRef={fileInputRef}
              isNextEnabled={isNextEnabled}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 2 && (
            <Step2
              formData={formData}
              setFormData={setFormData}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 3 && (
            <Step3
              formData={formData}
              setFormData={setFormData}
              setCurrentStep={setCurrentStep}
              isEditMode={true}
              gigId={gigId}
            />
          )}
        </div>
      </div>
    </AdminSidebarLayout>
  );
};

export default EditGig;
